import '../i18n/autoTranslate';
import React, { useMemo, useState } from "react";
import AppBackground from '../../components/ui/AppBackground.tsx';

import SpotlightCard from "../../components/ui/SpotlightCard";
import ProfileSettings from "./components/ProfileSettings";
import AccountSettings from "./components/AccountSettings";
import SupportSettings from "./components/SupportSettings";
import Header from "../../components/ui/Header";
import { User, Lock, HelpCircle } from "lucide-react";
import DotPattern from "../../components/lightswind/dot-pattern";
import { Tabs, TabsContent } from "../../components/ui/shadcn/tabs";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/shadcn/card";
import { Button } from "../../components/ui/shadcn/button";
import { Badge } from "../../components/ui/shadcn/badge";

export default function Setting() {
  const [activeSetting, setActiveSetting] = useState("profile");
  const activeMeta = useMemo(() => {
    if (activeSetting === "account") {
      return { title: "Account Settings", description: "Manage your account security and preferences.", icon: Lock };
    }
    if (activeSetting === "support") {
      return { title: "Support", description: "Get help or contact our support team.", icon: HelpCircle };
    }
    return { title: "Profile Settings", description: "Update your personal information below.", icon: User };
  }, [activeSetting]);

  const settingsActions = useMemo(
    () => [
      {
        value: "profile",
        title: "Profile",
        description: "Personal info, avatar, and contact details.",
        Icon: User,
        spotlight: "rgba(139, 92, 246, 0.2)",
        image: "/assets/images/Gemini_Generated_1_n9djp1.png",
      },
      {
        value: "account",
        title: "Account",
        description: "Password, security, and account controls.",
        Icon: Lock,
        spotlight: "rgba(99, 102, 241, 0.2)",
        image: "/assets/images/Gemini_Generated_3_qmjtpc.png",
      },
      {
        value: "support",
        title: "Support",
        description: "FAQs and reach our team for help.",
        Icon: HelpCircle,
        spotlight: "rgba(16, 185, 129, 0.2)",
        image: "/assets/images/Gemini_Generated_6_dlmovh.png",
      },
    ],
    []
  );

  return (
    <AppBackground className="relative overflow-hidden min-h-screen bg-[#0B0B12] text-white">
      <div className="pointer-events-none absolute inset-0 z-0" aria-hidden="true">
        <div className="absolute inset-0 bg-[radial-gradient(900px_520px_at_20%_10%,rgba(139,92,246,0.22),transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(900px_520px_at_80%_0%,rgba(99,102,241,0.16),transparent_62%)]" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/55" />
      </div>

      <main className="relative z-10 pt-6 pb-24 md:pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-6">
            <SpotlightCard spotlightColor="rgba(139, 92, 246, 0.18)" className="custom-spotlight-card">
              <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[rgba(30,30,46,0.75)] via-[rgba(30,30,46,0.55)] to-[rgba(46,46,69,0.30)] p-6 sm:p-8">
                <div className="absolute inset-0 opacity-35 pointer-events-none">
                  <img
                    src={"/assets/images/Gemini_Generated_4_munghs.png"}
                    className="absolute inset-0 h-full w-full object-cover scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-br from-black/10 via-black/55 to-black/70" />
                </div>
                <DotPattern className="opacity-15" />
                <div className="relative">
                  <div className="flex items-start justify-between gap-4">
                    <div className="space-y-1">
                      <h1 className="text-2xl sm:text-4xl font-semibold tracking-tight text-white">Settings</h1>
                      <p className="text-sm sm:text-base text-gray-200/80">
                        Manage your profile, security, and support preferences.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </SpotlightCard>

            <Tabs value={activeSetting} onValueChange={setActiveSetting} className="gap-6">
              <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
                <div className="space-y-4">
                  {settingsActions.map((action) => {
                    const isActive = activeSetting === action.value;
                    return (
                      <SpotlightCard
                        key={action.value}
                        spotlightColor={action.spotlight}
                        className="group custom-spotlight-card"
                      >
                        <button
                          type="button"
                          onClick={() => setActiveSetting(action.value)}
                          className={`relative w-full overflow-hidden rounded-2xl border bg-gradient-to-br from-[rgba(30,30,46,0.75)] via-[rgba(30,30,46,0.55)] to-[rgba(46,46,69,0.30)] p-5 text-left transition-all ${
                            isActive ? "border-purple-500/50" : "border-white/10 hover:border-purple-500/30"
                          }`}
                        >
                          <div className="absolute inset-0 opacity-35 pointer-events-none">
                            <img
                              src={action.image}
                              className="absolute inset-0 h-full w-full object-cover scale-110 group-hover:scale-125 transition-transform duration-500"
                            />
                            <div className="absolute inset-0 bg-gradient-to-br from-black/10 via-black/55 to-black/70" />
                          </div>

                          <div className="relative space-y-4">
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex items-center gap-3">
                                <div className={`flex h-11 w-11 items-center justify-center rounded-xl border ${
                                  isActive ? "border-purple-500/30 bg-purple-500/15" : "border-white/10 bg-white/5"
                                }`}>
                                  <action.Icon className="h-5 w-5 text-indigo-300" />
                                </div>
                                <div className="space-y-0.5">
                                  <div className="text-base font-semibold tracking-tight text-white">{action.title}</div>
                                  <div className="text-xs text-gray-200/70">{action.description}</div>
                                </div>
                              </div>
                              {isActive && (
                                <Badge className="bg-white/10 border-white/15 text-white" variant="outline">
                                  Active
                                </Badge>
                              )}
                            </div>

                            <Button
                              type="button"
                              variant="outline"
                              className="w-full justify-center bg-gradient-to-br from-[#8B7CFF] to-[#6C63FF] text-white border-white/10 hover:text-white hover:shadow-[0_0_50px_rgba(108,99,255,0.35)]"
                            >
                              {isActive ? "Editing" : "Open"}
                            </Button>
                          </div>
                        </button>
                      </SpotlightCard>
                    );
                  })}
                </div>

                <SpotlightCard spotlightColor="rgba(99, 102, 241, 0.15)" className="custom-spotlight-card">
                  <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[rgba(30,30,46,0.75)] via-[rgba(30,30,46,0.55)] to-[rgba(46,46,69,0.30)] backdrop-blur-md p-2">
                    <div className="absolute inset-0 opacity-25 pointer-events-none">
                      <img
                        src={"/assets/images/Gemini_Generated_2_zckc1w.png"}
                        className="absolute inset-0 h-full w-full object-cover scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-br from-black/30 via-black/55 to-black/75" />
                    </div>
                    <Card className="border-0 bg-transparent shadow-none py-2">
                      <CardHeader className="relative px-6 pb-4">
                        <CardTitle className="flex items-center gap-2 text-white text-xl font-semibold tracking-tight">
                          <activeMeta.icon className="h-5 w-5 text-indigo-400" />
                          <span>{activeMeta.title}</span>
                        </CardTitle>
                        <CardDescription className="text-gray-200/70">{activeMeta.description}</CardDescription>
                      </CardHeader>
                      <CardContent className="relative px-6 pb-6">
                        <TabsContent value="profile">
                          <ProfileSettings embedded />
                        </TabsContent>
                        <TabsContent value="account">
                          <AccountSettings embedded />
                        </TabsContent>
                        <TabsContent value="support">
                          <SupportSettings embedded />
                        </TabsContent>
                      </CardContent>
                    </Card>
                  </div>
                </SpotlightCard>
              </div>
            </Tabs>
          </div>
        </div>
      </main>
    </AppBackground>
  );
} 
